import { sources } from '../api/sources';
import { CACHED_PARTNER_ENROLLMENT_STATUS_KEY } from './constants';
import protect_my_id_img from '../assets/protect-my-id-img.svg';
import parking_spot_img from '../assets/parking-spot-img.svg';
import hertz_img from '../assets/hertz-img.svg';
import fully_enrolled_img from '../assets/fully_enrolled_img.svg';

import { IPartnerEnrollmentStatus, IPartnerIntegration, PartnerIntegration } from '@lib/types';
import { NewJoinSessionData, PartnerEnrollments } from '../application/models/NewJoinSessionData';

const pollForPartnerEnrollmentStatus = async (
  sessionId: string
): Promise<{
  pollingStatus: PartnerEnrollmentProcessStatus;
  partnerEnrollmentStatuses?: IPartnerEnrollmentStatus[];
}> => {
  return new Promise((resolve) => {
    const statuses = [] as IPartnerEnrollmentStatus[][];
    let retryCount = 0;
    const MAX_RETRIES = 10;
    const RETRY_INTERVAL = 5000;

    const interval = setInterval(async () => {
      if (retryCount >= MAX_RETRIES) {
        const partnerStatuses = statuses.length ? statuses[statuses.length - 1] : undefined;
        resolve({
          pollingStatus: PartnerEnrollmentProcessStatus.MAX_RETRIES,
          partnerEnrollmentStatuses: partnerStatuses,
        });
        clearInterval(interval);
      }

      const partnerEnrollmentStatusesResponse = await sources.getPartnerEnrollmentStatus({ sessionId });
      const partnerEnrollmentStatuses = partnerEnrollmentStatusesResponse?.partnerEnrollmentStatuses;

      const validEnrollmentStatuses = Array.isArray(partnerEnrollmentStatuses);

      if (validEnrollmentStatuses) {
        statuses.push(partnerEnrollmentStatuses);

        const partnerEnrollmentStatusComplete = partnerEnrollmentStatuses?.every(({ status }) => {
          return status === 'ENROLLMENT SUCCESS' || status === 'ENROLLMENT FAILED' || status === 'REQUEST FAILED';
        });

        if (partnerEnrollmentStatusComplete) {
          resolve({
            pollingStatus: PartnerEnrollmentProcessStatus.COMPLETED,
            partnerEnrollmentStatuses: partnerEnrollmentStatuses,
          });
          clearInterval(interval);
        }
      }

      retryCount++;
    }, RETRY_INTERVAL);
  });
};

export const getPartnerList = (partners: IPartnerIntegration[]) => {
  switch (partners.length) {
    case 0:
      return '';
    case 1:
      return accountLabel[partners[0].name];
    case 2:
      return `${accountLabel[partners[0].name]} and ${accountLabel[partners[1].name]}`;
    default:
      return `${partners
        .slice(0, partners.length - 1)
        .map((partner) => accountLabel[partner.name])
        .join(', ')} and ${accountLabel[partners[partners.length - 1].name]}`;
  }
};

export const getPartnerBrandList = (partners: PartnerIntegration[]) => {
  switch (partners.length) {
    case 0:
      return '';
    case 1:
      return brandMap[partners[0]];
    case 2:
      return `${brandMap[partners[0]]} and ${brandMap[partners[1]]}`;
    default:
      return `${partners
        .slice(0, partners.length - 1)
        .map((partner) => brandMap[partner])
        .join(', ')} and ${brandMap[partners[partners.length - 1]]}`;
  }
};

export const partnerHelper = {
  pollForPartnerEnrollmentStatus,
};

export enum PartnerEnrollmentProcessStatus {
  MAX_RETRIES = 'MAX_RETRIES',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

const experianBenefits = [
  'Activate your free ID theft protection',
  'Experian Credit Report (upon enrollment)',
  'Receive a complimentary exposure scan',
];

const parkingSpotBenefits = [
  'Receive 2 free days of airport parking when you join the Spot Club',
  'Waived reservation fees at The Parking Spot locations',
  'Save at least 10% every time you park',
];

const hertzBenefits = [
  'Get a free rental day',
  'Free reservations ($5.95)',
  '10% or more savings near-airport parking',
];

const experianLabel = 'ProtectMyID® Essential for AAA Members';
const experianBrandLabel = 'ProtectMyID®';

const parkingSpotLabel = 'The Parking Spot<sup style="font-size: 0.6rem; vertical-align: text-bottom">®</sup>';
const hertzLabel = 'Hertz Gold<sup style="font-size: 0.6rem; vertical-align: text-bottom">®</sup>';

const parkingSpotTitle = 'The Parking Spot® provides near-airport parking and shuttle service in the United States.';

const parkingSpotUrl =
  'https://www.theparkingspot.com/aaa?utm_campaign=aaa_20252_signup&utm_source=aaa_ace&utm_medium=api';

const experianUrl = 'https://www.ace.aaa.com/financial/idtheft.html#planSelect';
const hertzRegistrationUrl = 'https://www.aaa.com/HertzGold';

const parkingSpotLinkageUrl =
  'https://support.theparkingspot.com/hc/en-us/articles/360052863313-How-do-I-link-my-AAA-Membership-Number-to-my-existing-Spot-Club-account';
const hertzLinkageUrl = 'https://www.aaa.com/PremierGold/';

export const fullyEnrolledImg = fully_enrolled_img;

export const benefitsMap: Record<PartnerIntegration, string[]> = {
  [PartnerIntegration.PARKING_SPOT]: parkingSpotBenefits,
  [PartnerIntegration.EXPERIAN]: experianBenefits,
  [PartnerIntegration.HERTZ]: hertzBenefits,
};

export const imageMap: Record<PartnerIntegration, string> = {
  [PartnerIntegration.PARKING_SPOT]: parking_spot_img,
  [PartnerIntegration.EXPERIAN]: protect_my_id_img,
  [PartnerIntegration.HERTZ]: hertz_img,
};

export const labelMap: Record<string, string> = {
  [PartnerIntegration.PARKING_SPOT]: parkingSpotLabel,
  [PartnerIntegration.EXPERIAN]: experianLabel,
  [PartnerIntegration.HERTZ]: hertzLabel,
};

export const brandMap: Record<string, string> = {
  [PartnerIntegration.PARKING_SPOT]: parkingSpotLabel,
  [PartnerIntegration.EXPERIAN]: experianBrandLabel,
  [PartnerIntegration.HERTZ]: hertzLabel,
};

export const titleMap: Record<string, string | null> = {
  [PartnerIntegration.PARKING_SPOT]: parkingSpotTitle,
  [PartnerIntegration.EXPERIAN]: null,
  [PartnerIntegration.HERTZ]: '',
};

export const registrationLinkMap: Record<string, string> = {
  [PartnerIntegration.PARKING_SPOT]: parkingSpotUrl,
  [PartnerIntegration.EXPERIAN]: experianUrl,
  [PartnerIntegration.HERTZ]: hertzRegistrationUrl,
};

export const linkageUrlMap: Record<string, string> = {
  [PartnerIntegration.PARKING_SPOT]: parkingSpotLinkageUrl,
  [PartnerIntegration.EXPERIAN]: experianUrl,
  [PartnerIntegration.HERTZ]: hertzLinkageUrl,
};

export const accountLabel = {
  [PartnerIntegration.PARKING_SPOT]: 'Spot Club',
  [PartnerIntegration.EXPERIAN]: 'Experian',
  [PartnerIntegration.HERTZ]: 'Hertz Gold',
};

export const partnerName = {
  [PartnerIntegration.PARKING_SPOT]: 'The Parking Spot',
  [PartnerIntegration.EXPERIAN]: 'Experian',
  [PartnerIntegration.HERTZ]: 'Hertz Gold',
};

export const getPartnerEnrollmentStatuses = (sessionData: NewJoinSessionData) => {
  const partnerOffers = sessionData.partnerOffers;
  const partnerEnrollments = sessionData.formData.partnerEnrollments || ({} as PartnerEnrollments);

  const failedPartnerLinkages = [] as IPartnerIntegration[];
  const failedPartnerRegistrations = [] as IPartnerIntegration[];
  const successfulPartnerLinkages = [] as IPartnerIntegration[];
  const successfulPartnerRegistrations = [] as IPartnerIntegration[];

  for (let partner of Object.keys(partnerEnrollments)) {
    const enrolled = partnerEnrollments[partner as PartnerIntegration];
    if (!enrolled) {
      continue;
    }

    const partnerOffer = partnerOffers?.availableOffers?.find((offer) => offer.name === partner);
    const partnerStatus = partnerOffers?.partnerStatuses?.find((partnerStatus) => partner === partnerStatus.partner);
    if (partnerStatus?.status === 'ENROLLMENT SUCCESS') {
      if (partnerOffer?.hasExistingAccount) {
        successfulPartnerLinkages.push(partnerOffer as IPartnerIntegration);
      } else {
        successfulPartnerRegistrations.push(partnerOffer as IPartnerIntegration);
      }
    } else {
      if (partnerOffer?.hasExistingAccount) {
        failedPartnerLinkages.push(partnerOffer as IPartnerIntegration);
      } else {
        failedPartnerRegistrations.push(partnerOffer as IPartnerIntegration);
      }
    }
  }

  return {
    failedPartnerLinkages,
    failedPartnerRegistrations,
    successfulPartnerLinkages,
    successfulPartnerRegistrations,
  };
};
